import { useTranslation } from '@jq4race/i18n';
import type { EventGuestResponse } from '@jq4race/shared';
import {
  Badge,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@jq4race/ui';
import { Users } from 'lucide-react';

import { guestStatusLabel } from '../constants';

interface GuestsTableProps {
  guests: EventGuestResponse[];
  loading: boolean;
}

export function GuestsTable({ guests, loading }: GuestsTableProps) {
  const { t } = useTranslation('events');
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('guestsTable.name')}</TableHead>
          <TableHead>{t('guestsTable.plate')}</TableHead>
          <TableHead>{t('guestsTable.status')}</TableHead>
          <TableHead>{t('guestsTable.billing')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading && guests.length === 0 ? (
          <SkeletonRows />
        ) : guests.length === 0 ? (
          <TableEmpty colSpan={4}>
            <EmptyState />
          </TableEmpty>
        ) : (
          guests.map((guest) => {
            // guestStatusLabel returns the internal 'Arrived'/'Invited' marker
            // used for badge tone selection; the displayed text is translated
            // separately via constants.guestStatus.*.
            const status = guestStatusLabel(guest.arrivedAt);
            return (
              <TableRow key={guest.id}>
                <TableCell>
                  <span className="font-medium text-jq-charcoal">{guest.name}</span>
                </TableCell>
                <TableCell className="text-jq-graphite">{guest.plate ?? '—'}</TableCell>
                <TableCell>
                  <Badge variant={status === 'Arrived' ? 'success' : 'secondary'}>
                    {status === 'Arrived'
                      ? t('constants.guestStatus.arrived')
                      : t('constants.guestStatus.invited')}
                  </Badge>
                </TableCell>
                <TableCell className="text-jq-graphite">
                  {guest.billingMode ? t(`constants.guestBillingMode.${guest.billingMode}`) : '—'}
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positional
        <TableRow key={`skeleton-${i}`}>
          {Array.from({ length: 4 }).map((_, j) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positional
            <TableCell key={`cell-${j}`}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function EmptyState() {
  const { t } = useTranslation('events');
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <Users className="h-10 w-10 text-jq-stone" strokeWidth={1.25} />
      <p className="text-[14px] font-medium text-jq-charcoal">{t('guestsTable.empty.title')}</p>
      <p className="max-w-sm text-[13px] text-jq-graphite">{t('guestsTable.empty.description')}</p>
    </div>
  );
}
